import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { Inbox, Clock, CheckCircle, TrendingUp, Award, Palette } from "lucide-react";
import { api } from "../../lib/api";
import { AdminLayout } from "./AdminLayout";

export default function AdminDashboard() {
  const { data: a } = useQuery({ queryKey: ["analytics"], queryFn: async () => (await api.get("/admin/analytics")).data });
  const { data: artists = [] } = useQuery({ queryKey: ["admin-artists"], queryFn: async () => (await api.get("/admin/artists")).data });
  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: async () => (await api.get("/styles")).data });

  const artistMap = Object.fromEntries(artists.map((x) => [x.slug, x.name]));
  const styleMap = Object.fromEntries(styles.map((x) => [x.slug, x.name]));

  if (!a) return <AdminLayout title="Dashboard"><p className="text-[#828290] font-mono text-sm">Loading…</p></AdminLayout>;

  const kpis = [
    { label: "Total bookings", value: a.total, icon: Inbox },
    { label: "Pending / New", value: (a.by_status.New || 0) + (a.by_status.Pending || 0), icon: Clock },
    { label: "Confirmed", value: a.by_status.Confirmed || 0, icon: CheckCircle },
    { label: "This week", value: a.week, icon: TrendingUp },
  ];

  const artistChart = a.by_artist.slice(0, 8).map((x) => ({ name: x.artist === "unassigned" ? "Unassigned" : (artistMap[x.artist] || x.artist), count: x.count }));
  const styleChart = a.by_style.slice(0, 8).map((x) => ({ name: styleMap[x.style] || x.style, count: x.count }));

  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map((k) => (
          <div key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="border border-ash bg-charcoal p-6">
            <k.icon size={18} className="text-ember mb-4" />
            <div className="font-serif text-5xl text-white leading-none">{k.value}</div>
            <div className="mt-3 font-mono text-[0.6rem] uppercase tracking-widest text-[#828290]">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <Highlight icon={Award} label="Most requested artist" value={a.top_artist ? (artistMap[a.top_artist] || a.top_artist) : "—"} />
        <Highlight icon={Palette} label="Most requested style" value={a.top_style ? (styleMap[a.top_style] || a.top_style) : "—"} />
        <Highlight icon={TrendingUp} label="Conversion rate" value={`${a.conversion}%`} sub="Confirmed + completed" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Bookings by artist" data={artistChart} />
        <ChartCard title="Bookings by style" data={styleChart} />
      </div>

      <div className="mt-8 border border-ash bg-charcoal p-6">
        <p className="eyebrow mb-6">Pipeline</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {["New", "Pending", "Confirmed", "Completed", "Cancelled"].map((s) => (
            <div key={s} className="border border-ash p-4">
              <div className="font-serif text-3xl text-white">{a.by_status[s] || 0}</div>
              <div className="mt-2 font-mono text-[0.6rem] uppercase tracking-widest text-[#828290]">{s}</div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}

function Highlight({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-ash bg-charcoal p-6">
      <Icon size={18} className="text-ember mb-4" />
      <div className="font-serif text-3xl text-white capitalize">{value}</div>
      <div className="mt-2 font-mono text-[0.6rem] uppercase tracking-widest text-[#828290]">{label}</div>
      {sub && <div className="text-[#555562] text-xs mt-1">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, data }) {
  return (
    <div className="border border-ash bg-charcoal p-6">
      <p className="eyebrow mb-6">{title}</p>
      {data.length === 0 ? (
        <p className="text-[#555562] text-sm">No data yet.</p>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" width={100} tick={{ fill: "#C4C4CC", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "#1A1A22" }} contentStyle={{ background: "#08080A", border: "1px solid #282832", color: "#fff", fontSize: 12 }} />
            <Bar dataKey="count" radius={[0, 0, 0, 0]}>
              {data.map((_, i) => <Cell key={i} fill="#A855F7" />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
